import React from 'react'
import { styled } from '@mui/material/styles'

import AddIcon from '@mui/icons-material/Add'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import MuiAccordionSummary, { AccordionSummaryProps, accordionSummaryClasses } from '@mui/material/AccordionSummary'

const AccordionExpandContext = React.createContext<() => void>(() => undefined)

const ExpandableAccordion = (props: AccordionProps) => {
    const { expanded: controlledExpanded, defaultExpanded, onChange, ...rest } = props
    const [uncontrolledExpanded, setUncontrolledExpanded] = React.useState(!!defaultExpanded)
    const isControlled = controlledExpanded !== undefined
    const expanded = isControlled ? controlledExpanded : uncontrolledExpanded

    const handleChange = React.useCallback(
        (event: React.SyntheticEvent, value: boolean) => {
            !isControlled && setUncontrolledExpanded(value)
            onChange?.(event, value)
        },
        [isControlled, onChange]
    )

    const expand = React.useCallback(() => {
        !isControlled && setUncontrolledExpanded(true)
    }, [isControlled])

    return (
        <AccordionExpandContext.Provider value={expand}>
            <MuiAccordion disableGutters elevation={0} square {...rest} expanded={expanded} onChange={handleChange} />
        </AccordionExpandContext.Provider>
    )
}

const Accordion = styled(ExpandableAccordion)({
    margin: 0,
    border: '1px solid var(--border)',
    borderRadius: 11,
    backgroundColor: '#fbfcfb',
    backgroundImage: 'none',
    boxShadow: 'none',
    overflow: 'hidden',
    '&::before': {
        display: 'none'
    },
    '&.Mui-expanded': {
        borderColor: '#cbdccd'
    }
})

const GroupActionButton = styled(IconButton)({
    width: 30,
    height: 30,
    borderRadius: 8,
    color: '#2d332f',
    '&:hover': { backgroundColor: '#edf1ee' },
    '& .MuiSvgIcon-root': { fontSize: 18 }
})

const ExpandWithActions = ({ onClear, onAdd }: { onClear?: () => void; onAdd?: () => void }) => {
    const expand = React.useContext(AccordionExpandContext)
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'var(--muted)' }}>
            {onAdd && (
                <Tooltip title="Adicionar condição">
                    <GroupActionButton
                        size="small"
                        aria-label="Adicionar condição"
                        onClick={(e) => {
                            e.stopPropagation()
                            expand()
                            onAdd?.()
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </GroupActionButton>
                </Tooltip>
            )}
            {onClear && (
                <Tooltip title="Limpar filtro">
                    <GroupActionButton
                        size="small"
                        aria-label="Limpar filtro"
                        onClick={(e) => {
                            e.stopPropagation()
                            onClear?.()
                        }}
                    >
                        <DeleteSweepIcon fontSize="small" />
                    </GroupActionButton>
                </Tooltip>
            )}

            <ExpandMoreIcon className="expand-arrow" fontSize="small" />
        </Box>
    )
}

interface CustomAccordionSummaryProps extends AccordionSummaryProps {
    caption?: string
    captionBold?: boolean
    onClear?: () => void
    onAdd?: () => void
}

const GroupCaption = styled('h4', { shouldForwardProp: (prop) => prop !== 'active' })<{ active?: boolean }>(
    ({ active }) => ({
        margin: 0,
        fontSize: 13,
        lineHeight: 1.3,
        fontWeight: active ? 700 : 500,
        color: active ? 'var(--green-800)' : 'var(--ink)'
    })
)

const AccordionSummary = styled((props: CustomAccordionSummaryProps) => {
    const { onClear, onAdd, caption, captionBold, ...rest } = props

    // component="div": a partir do MUI 6 o summary é um <button>, e os botões de ação do expandIcon
    // ficariam aninhados em outro <button> (HTML inválido). O MUI 5 já renderizava como div.
    return (
        <MuiAccordionSummary
            component="div"
            expandIcon={<ExpandWithActions onClear={onClear} onAdd={onAdd} />}
            {...rest}
        >
            {caption && <GroupCaption active={captionBold}>{caption}</GroupCaption>}
        </MuiAccordionSummary>
    )
})({
    minHeight: '44px !important',
    padding: '0 10px 0 14px',
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: '#f4f7f5' },
    [`&.${accordionSummaryClasses.expanded}`]: {
        borderBottom: '1px solid var(--border)'
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}`]: {
        transform: 'none'
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
        transform: 'none'
    },
    [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded} .expand-arrow`]: {
        transform: 'rotate(180deg)'
    },
    [`& .expand-arrow`]: {
        transition: 'transform 200ms'
    },
    [`& .${accordionSummaryClasses.content}`]: {
        margin: 0,
        minWidth: 0
    }
})

const AccordionDetails = styled(MuiAccordionDetails)({
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    padding: 14,
    margin: 0,
    backgroundColor: 'var(--surface)'
})

export const FilterAccordion = Accordion
export const FilterAccordionSummary = AccordionSummary
export const FilterAccordionDetails = AccordionDetails
